/**
 * RSS Parser Utility
 * Convert RSS feeds to array of items for display in Kiosk
 */

/**
 * Parse RSS feed XML to JSON
 * @param {string} xmlText - Raw RSS XML text
 * @returns {Array} Array of items with title, description, link, pubDate, image
 */
export function parseRSSXML(xmlText) {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    // Check for parsing errors
    if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
      throw new Error('Invalid RSS XML');
    }

    const items = [];
    const itemElements = xmlDoc.getElementsByTagName('item');

    for (let i = 0; i < itemElements.length; i++) {
      const item = itemElements[i];
      const title = getText(item, 'title');
      const description = getText(item, 'description');
      const link = getText(item, 'link');
      const pubDate = getText(item, 'pubDate');
      const author = getText(item, 'author');
      const category = getText(item, 'category');
      const comments = getText(item, 'comments');
      const guid = getText(item, 'guid');

      // Try to extract image from content:encoded, description, or media:content
      let image = null;
      const mediaContent = item.getElementsByTagName('media:content');
      if (mediaContent.length > 0) {
        image = mediaContent[0].getAttribute('url');
      } else {
        // Try to extract from description
        const descMatch = description ? description.match(/<img[^>]+src="([^">]+)"/i) : null;
        if (descMatch) {
          image = descMatch[1];
        }
      }

      items.push({
        title: title || 'Untitled',
        description: stripHTML(description || ''),
        link: link || '',
        pubDate: pubDate || '',
        author: author || '',
        category: category || '',
        image: image || '',
        comments: comments || '',
        guid: guid || '',
        descriptionHTML: description || '', // Store original HTML
      });
    }

    return items;
  } catch (error) {
    console.error('Error parsing RSS:', error);
    return [];
  }
}

/**
 * Fetch and parse RSS feed from URL
 * @param {string} feedUrl - URL of RSS feed
 * @returns {Promise<Array>} Promise resolving to array of items
 */
export async function fetchRSSFeed(feedUrl) {
  try {
    if (!feedUrl || typeof feedUrl !== 'string') {
      throw new Error('Invalid feed URL');
    }

    // List of CORS proxy services to try in order
    const proxies = [
      (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`, // Most reliable
      (url) => `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
      (url) => `https://thingproxy.freeboard.io/fetch/${url}`,
    ];

    let lastError = null;

    // Try each CORS proxy
    for (const proxyFn of proxies) {
      try {
        const proxyUrl = proxyFn(feedUrl);
        const response = await fetch(proxyUrl, {
          headers: {
            'Accept': 'application/rss+xml, application/atom+xml, application/xml, text/xml',
          },
        });

        if (response.ok) {
          const text = await response.text();
          const parsed = parseRSSXML(text);
          if (parsed.length > 0) {
            return parsed;
          }
        }
      } catch (e) {
        lastError = e;
        continue;
      }
    }

    // Last resort: try direct fetch (works if server doesn't require CORS)
    try {
      const response = await fetch(feedUrl, {
        headers: {
          'Accept': 'application/rss+xml, application/atom+xml, application/xml, text/xml',
        },
      });
      if (response.ok) {
        const text = await response.text();
        return parseRSSXML(text);
      }
    } catch (e) {
      lastError = e;
    }

    throw lastError || new Error('Failed to fetch RSS feed from all sources');
  } catch (error) {
    console.error('Error fetching RSS feed:', error);
    return [];
  }
}

/**
 * Get text content from XML element
 * @param {Element} element - XML element
 * @param {string} tagName - Tag name to search for
 * @returns {string} Text content
 */
function getText(element, tagName) {
  const elements = element.getElementsByTagName(tagName);
  if (elements.length > 0) {
    return elements[0].textContent || '';
  }
  return '';
}

/**
 * Strip HTML tags from string
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
function stripHTML(html) {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  let text = temp.textContent || temp.innerText || '';
  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return text.trim();
}

/**
 * Format date string to readable format
 * @param {string} dateStr - Date string from RSS (RFC 2822)
 * @returns {string} Formatted date
 */
export function formatRSSDate(dateStr) {
  try {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Transform RSS items for display
 * @param {Array} items - RSS items
 * @param {number} limit - Max items to return
 * @returns {Array} Transformed items
 */
export function transformRSSItems(items, limit = 20) {
  return items.slice(0, limit).map((item, idx) => ({
    id: item.guid || `${item.title}-${idx}`,
    title: item.title,
    description: item.description,
    shortDescription: truncate(item.description, 100),
    link: item.link,
    pubDate: item.pubDate,
    pubDateFormatted: formatRSSDate(item.pubDate),
    author: item.author,
    category: item.category,
    image: item.image,
  }));
}

/**
 * Truncate text to max length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Max length
 * @returns {string} Truncated text
 */
function truncate(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}
